import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useScan } from '../ui/useScan';
import { Badge, Card, FindingRow, SignalValueText } from '../ui/components';
import { AiPanel } from '../ui/AiPanel';
import { ExportButtons } from '../ui/ExportButtons';
import { getProbe } from '../engine/registry';
import type { ScanStatus, SignalValue } from '../engine/types';

const STATUS_LABEL: Record<ScanStatus, string> = {
  IDLE: 'Idle',
  COLLECTING: 'Collecting…',
  COLLECT_SUCCESSFUL: 'Collected',
  COLLECT_PARTIAL: 'Partially collected',
  COLLECT_FAILED: 'Collection failed',
};

function SignalRow({ name, sig }: { name: string; sig: SignalValue }) {
  return (
    <div className="grid grid-cols-1 gap-1 border-t border-white/5 py-2 first:border-t-0 sm:grid-cols-[minmax(0,15rem)_1fr_auto] sm:items-baseline sm:gap-3">
      <div className="flex items-center gap-2">
        <span className="font-mono text-xs text-zinc-400">{name}</span>
        {sig.origin === 'edge' && (
          <Badge tone="accent" title="Relayed from the edge Worker">
            edge
          </Badge>
        )}
        {sig.origin === 'derived' && <span className="text-[11px] text-zinc-600">derived</span>}
      </div>
      <div className="min-w-0 font-mono text-xs leading-5">
        <SignalValueText value={sig.value} />
        {sig.error && <div className="mt-0.5 text-zinc-500">{sig.error}</div>}
      </div>
      <div className="flex items-center gap-2 sm:justify-end">
        {sig.status !== 'ok' && (
          <Badge tone={sig.status === 'error' ? 'error' : sig.status === 'denied' ? 'denied' : 'unavailable'}>
            {sig.status}
          </Badge>
        )}
        {sig.durationMs > 0 && (
          <span className="text-[11px] tabular-nums text-zinc-600">{Math.round(sig.durationMs)}ms</span>
        )}
      </div>
    </div>
  );
}

export function Dashboard() {
  const { result, scanning, error, start } = useScan();

  const findings = useMemo(() => result?.findings ?? [], [result]);
  const sortedFindings = useMemo(
    () => [...findings].sort((a, b) => b.severity.localeCompare(a.severity)),
    [findings],
  );
  const highCount = findings.filter((f) => f.severity === 'high').length;

  const signalCount = result
    ? Object.values(result.signals).reduce((a, s) => a + Object.keys(s).length, 0)
    : 0;
  const probeCount = result ? Object.keys(result.signals).length : 0;

  return (
    <div className="space-y-8">
      <div className="space-y-3">
        <h1 className="max-w-3xl text-3xl font-semibold tracking-tight text-white sm:text-4xl">
          Browser fingerprint scanner
        </h1>
        <p className="max-w-4xl text-sm leading-6 text-zinc-400">
          A static playground for browser fingerprinting and bot-detection signals: GPU, media and
          codec pipelines, engine micro-task timing, font shaping, WebRTC/ICE, cross-context
          coherence, and TLS edge metadata. Everything runs in your browser — see the{' '}
          <Link to="/techniques" className="text-orange-300 underline underline-offset-4">
            full technique coverage catalog
          </Link>
          .
        </p>
      </div>

      {/* Status header */}
      <Card>
        <div className="flex flex-wrap items-center gap-2.5">
          <Badge tone={result?.status === 'COLLECT_SUCCESSFUL' ? 'ok' : scanning ? 'denied' : 'unavailable'}>
            {STATUS_LABEL[result?.status ?? (scanning ? 'COLLECTING' : 'IDLE')]}
          </Badge>
          <Badge tone="neutral" title="Higher means more automation-like">
            Bot score{' '}
            <span className={result ? '' : 'text-zinc-500'}>
              {result ? `${result.botScore}/100` : '—'}
            </span>
          </Badge>
          {highCount > 0 && <Badge tone="error">{highCount} high-severity</Badge>}
          <div className="ml-auto flex items-center gap-2">
            <ExportButtons result={result} />
            <button
              type="button"
              onClick={() => void start()}
              disabled={scanning}
              className="rounded-lg border border-orange-500/25 bg-orange-500/10 px-3 py-1.5 text-sm font-medium text-orange-200 transition-colors hover:bg-orange-500/20 disabled:opacity-50"
            >
              {scanning ? 'Scanning…' : 'Rescan'}
            </button>
          </div>
        </div>

        <div className="mt-5 grid gap-3 lg:grid-cols-2">
          <div className="space-y-3">
            <div className="text-[11px] uppercase tracking-widest text-zinc-500">Fingerprint hash</div>
            <div className="break-all rounded-2xl border border-orange-500/15 bg-black/40 p-3 font-mono text-sm text-orange-100">
              {result?.fingerprintHash || (scanning ? 'collecting…' : '—')}
            </div>
          </div>
          <div className="space-y-3">
            <div className="text-[11px] uppercase tracking-widest text-zinc-500">Fingerprint ID</div>
            <div className="break-all rounded-2xl border border-white/8 bg-black/30 p-3 font-mono text-xs text-zinc-300">
              {result?.fingerprintId || (scanning ? 'collecting…' : 'not collected (persistence disabled)')}
            </div>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label="Probes" value={String(probeCount)} />
          <Stat label="Signals" value={String(signalCount)} />
          <Stat
            label="Parts"
            value={result ? String(Object.keys(result.fingerprintParts).length) : '—'}
          />
          <Stat label="Findings" value={String(findings.length)} />
        </div>

        {error && <p className="mt-4 text-sm text-rose-300">Scan error: {error}</p>}

        {result && (
          <div className="mt-5 space-y-2">
            <div className="text-[11px] uppercase tracking-widest text-zinc-500">
              Bot score by layer
            </div>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
              {result.reasons.map((r) => (
                <div key={r.category} className="rounded-lg border border-white/6 bg-black/20 p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs capitalize text-zinc-400">{r.category}</span>
                    <Badge tone={r.level === 'high' ? 'error' : r.level === 'medium' ? 'denied' : 'ok'}>
                      {r.level}
                    </Badge>
                  </div>
                  <div className="mt-1 font-mono text-sm tabular-nums text-zinc-200">
                    +{Math.round(r.score)}
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-zinc-500">
              Each layer reports how much it contributed. The individual rules and their thresholds
              are deliberately not exposed. A score is a summary of evidence, not a verdict.
            </p>
          </div>
        )}
      </Card>

      {/* Fingerprint parts */}
      {result && Object.keys(result.fingerprintParts).length > 0 && (
        <Card
          title="Fingerprint parts"
          subtitle="Normalized sub-hashes. A change here isolates which subsystem moved between runs."
          actions={
            <Link to="/compare" className="text-sm text-orange-300 underline underline-offset-4">
              Compare runs
            </Link>
          }
        >
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
            {Object.entries(result.fingerprintParts).map(([part, hash]) => (
              <div key={part} className="rounded-lg border border-white/6 bg-black/20 p-2.5">
                <div className="text-[11px] uppercase tracking-wide text-zinc-500">{part}</div>
                <div className="mt-0.5 font-mono text-xs text-zinc-200">{hash}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Cross-context */}
      {result && (
        <Card
          title="Cross-context consistency"
          subtitle="The same navigator/device values read in the main page, an iframe, and a worker."
        >
          {!result.crossContext.available ? (
            <p className="text-sm text-zinc-500">
              Additional contexts unavailable (iframe or worker blocked).
            </p>
          ) : result.crossContext.divergences.length === 0 ? (
            <div className="flex items-center gap-2 text-sm text-emerald-300">
              <Badge tone="ok">consistent</Badge>
              No divergence across {result.crossContext.contexts.length} contexts.
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-amber-200">
                {result.crossContext.divergences.length} value(s) differ between execution contexts —
                a common sign of partial spoofing.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="border-b border-white/10 text-zinc-500">
                      <th className="py-1.5 pr-3 font-medium">signal</th>
                      <th className="py-1.5 pr-3 font-medium">main</th>
                      <th className="py-1.5 pr-3 font-medium">iframe</th>
                      <th className="py-1.5 font-medium">worker</th>
                    </tr>
                  </thead>
                  <tbody className="font-mono text-zinc-300">
                    {result.crossContext.divergences.map((d) => (
                      <tr key={d.signal} className="border-b border-white/5">
                        <td className="py-1.5 pr-3 text-zinc-400">{d.signal}</td>
                        <td className="py-1.5 pr-3">{fmt(d.main)}</td>
                        <td className="py-1.5 pr-3">{fmt(d.iframe)}</td>
                        <td className="py-1.5">{fmt(d.worker)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Findings */}
      {result && (
        <Card
          title="Detection findings"
          subtitle={`${findings.length} finding(s) across hardware, browser, network and behaviour layers.`}
        >
          {findings.length === 0 ? (
            <div className="flex items-center gap-2 text-sm text-emerald-300">
              <Badge tone="ok">clean</Badge>
              No detection rules fired. This does not prove you are not a bot — it means none of
              the implemented checks found a contradiction.
            </div>
          ) : (
            <div className="space-y-2">
              {sortedFindings.map((f) => (
                <FindingRow key={f.id} finding={f} />
              ))}
            </div>
          )}
        </Card>
      )}

      {/* AI verdict */}
      <AiPanel result={result} />

      {/* All signals by probe */}
      {result && (
        <Card
          title="All signals"
          subtitle="Raw values grouped by probe. Expand any probe to inspect every measured value."
        >
          <div className="space-y-2">
            {Object.entries(result.signals).map(([probeId, sigs]) => (
              <SignalGroup key={probeId} probeId={probeId} signals={sigs} />
            ))}
          </div>
        </Card>
      )}

      {!result && !error && (
        <Card>
          <p className="text-sm text-zinc-500">{scanning ? 'Collecting signals…' : 'No scan yet.'}</p>
        </Card>
      )}
    </div>
  );
}

function SignalGroup({
  probeId,
  signals,
}: {
  probeId: string;
  signals: Record<string, SignalValue>;
}) {
  const entries = Object.entries(signals);
  const probe = getProbe(probeId);
  if (entries.length === 0) return null;
  return (
    <details className="rounded-xl border border-white/6 bg-black/20">
      <summary className="flex cursor-pointer list-none flex-wrap items-center gap-2.5 px-4 py-3">
        <span className="text-sm font-medium text-zinc-100">{probe?.label ?? probeId}</span>
        <span className="text-xs text-zinc-500">{probe?.description}</span>
        <span className="ml-auto text-xs tabular-nums text-zinc-500">
          {entries.length} signal{entries.length === 1 ? '' : 's'}
        </span>
      </summary>
      <div className="px-4 pb-4">
        <div className="divide-y divide-white/5">
          {entries.map(([name, sig]) => (
            <SignalRow key={name} name={name} sig={sig} />
          ))}
        </div>
      </div>
    </details>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-white/6 bg-black/20 p-3">
      <div className="text-[11px] uppercase tracking-wide text-zinc-500">{label}</div>
      <div className="mt-0.5 font-mono text-lg tabular-nums text-zinc-100">{value}</div>
    </div>
  );
}

function fmt(v: unknown): string {
  if (v === undefined) return '—';
  if (v === null) return 'null';
  if (typeof v === 'string') return v;
  return JSON.stringify(v);
}
